import { useEffect, useRef, useState } from 'react'
import { Modal, Select, Stack, TextInput, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { showNotification } from '@mantine/notifications'
import { formatHumanName, normalizeErrorString } from '@medplum/core'
import type { Patient, Practitioner } from '@medplum/fhirtypes'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import { IconCircleCheck, IconCircleOff } from '@tabler/icons-react'
import { useMiddlewareClient } from '@/hooks'
import { logError } from '@/utils/errorUtils'
import { VoiceInputButton, type VoiceInputButtonHandle } from '@/components/ui'
import {
  getOrganizationPractitioners,
  type PractitionerThreadOption,
} from '@/pages/Messages/service'
import { PatientSelect } from '@/pages/CallCenterHome/components/PatientSelect/PatientSelect'

type FormValues = {
  recipientId: string
  subject: string
  message: string
}

type ComposeMessageModalProps = {
  opened: boolean
  onClose: () => void
  onCreated?: () => void
}

function getPractitionerLabel(p: Practitioner): string {
  return formatHumanName(p.name?.[0]) || p.id || 'Unnamed practitioner'
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 text-[11px] font-semibold tracking-wider text-gray-500 uppercase">
      {children}
    </p>
  )
}

export const ComposeMessageModal = ({ opened, onClose, onCreated }: ComposeMessageModalProps) => {
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()
  const profile = useMedplumProfile()
  const practitionerProfile = profile?.resourceType === 'Practitioner' ? profile : undefined

  const [submitting, setSubmitting] = useState(false)
  const [loadingPractitioners, setLoadingPractitioners] = useState(false)
  const [practitioners, setPractitioners] = useState<PractitionerThreadOption[]>([])
  const [micError, setMicError] = useState<string | null>(null)
  const voiceInputRef = useRef<VoiceInputButtonHandle>(null)
  const messageRef = useRef('')
  const stoppingVoiceRef = useRef(false)

  // Tags — identical BL to CreateThreadBtn
  const [isMarkedAsUrgent, setIsMarkedAsUrgent] = useState(false)
  const [isMarkedAsFollowUp, setIsMarkedAsFollowUp] = useState(false)

  // Subject patient — identical BL to CreateThreadBtn
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)

  const form = useForm<FormValues>({
    initialValues: { recipientId: '', subject: '', message: '' },
    validate: {
      recipientId: (v) => (!v ? 'Select a recipient.' : null),
      subject: (v) => (v.trim().length < 3 ? 'Subject must be at least 3 characters.' : null),
      message: (v) => (v.trim().length < 3 ? 'Message must be at least 3 characters.' : null),
    },
  })

  // Load practitioners when modal opens — same as CreateThreadBtn
  useEffect(() => {
    if (!opened || !practitionerProfile?.id) return
    let cancelled = false
    setLoadingPractitioners(true)

    getOrganizationPractitioners(medplum, practitionerProfile.id)
      .then((result) => {
        if (!cancelled) setPractitioners(result)
      })
      .catch((err) => {
        if (!cancelled) {
          logError('home', 'Failed to load practitioners', err)
          showNotification({ color: 'red', message: normalizeErrorString(err) })
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPractitioners(false)
      })

    return () => {
      cancelled = true
    }
  }, [opened, medplum, practitionerProfile?.id])

  const handleClose = () => {
    form.reset()
    setIsMarkedAsUrgent(false)
    setIsMarkedAsFollowUp(false)
    setSelectedPatient(null)
    setMicError(null)
    messageRef.current = ''
    onClose()
  }

  const submitForm = async (values: FormValues): Promise<void> => {
    if (!practitionerProfile?.id) return
    const recipient = practitioners.find((p) => p.practitioner.id === values.recipientId)
    // `Practitioner.id` is optional in FHIR, so it is narrowed here rather than
    // sending `undefined` inside the recipient list.
    const recipientId = recipient?.practitioner.id
    if (!recipient || !recipientId) {
      form.setFieldError('recipientId', 'Select a valid recipient.')
      return
    }

    setSubmitting(true)
    try {
      await middleware.post('/threads', {
        recipientPractitionerIds: [recipientId],
        subject: values.subject.trim(),
        initialMessage: messageRef.current.trim(),
        isMarkedAsUrgent,
        isMarkedAsFollowUp,
        subjectPatientId: selectedPatient?.id,
      })
      showNotification({
        color: 'green',
        icon: <IconCircleCheck size={16} />,
        title: 'Message sent',
        message: `Message sent to ${getPractitionerLabel(recipient.practitioner)}.`,
      })
      onCreated?.()
      handleClose()
    } catch (err) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff size={16} />,
        title: 'Failed to send',
        message: normalizeErrorString(err),
      })
    } finally {
      setSubmitting(false)
    }
  }

  const submitWithVoice = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (submitting || stoppingVoiceRef.current) {
      return
    }

    stoppingVoiceRef.current = true
    try {
      await voiceInputRef.current?.stop()
      setMicError(null)
      form.setFieldValue('message', messageRef.current)

      const { hasErrors } = form.validate()
      if (hasErrors) {
        return
      }

      await submitForm(form.getValues())
    } finally {
      stoppingVoiceRef.current = false
    }
  }

  const recipientOptions = practitioners.map((item) => ({
    value: item.practitioner.id as string,
    label: getPractitionerLabel(item.practitioner),
  }))

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      title={<span className="text-base font-semibold text-gray-900">New Message</span>}
      size="480px"
      radius="md"
    >
      <form onSubmit={submitWithVoice}>
        <Stack gap="md">
          {/* TO */}
          <div>
            <FieldLabel>To</FieldLabel>
            <Select
              placeholder="Search provider name…"
              searchable
              data={recipientOptions}
              disabled={loadingPractitioners}
              nothingFoundMessage={loadingPractitioners ? 'Loading…' : 'No results'}
              classNames={{ input: 'rounded-md border-gray-200 text-sm' }}
              {...form.getInputProps('recipientId')}
            />
          </div>

          {/* SUBJECT */}
          <div>
            <FieldLabel>Subject</FieldLabel>
            <TextInput
              placeholder="Message subject"
              classNames={{ input: 'rounded-md border-gray-200 text-sm' }}
              {...form.getInputProps('subject')}
            />
          </div>

          {/* TAGS — same toggle logic as CreateThreadBtn */}
          <div>
            <FieldLabel>Tags</FieldLabel>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIsMarkedAsUrgent((v) => !v)}
                className={[
                  'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                  isMarkedAsUrgent
                    ? 'border-red-500 bg-red-500 text-white'
                    : 'border-red-300 text-red-600 hover:bg-red-50',
                ].join(' ')}
              >
                {isMarkedAsUrgent ? '✓ Urgent' : 'Urgent'}
              </button>
              <button
                type="button"
                onClick={() => setIsMarkedAsFollowUp((v) => !v)}
                className={[
                  'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                  isMarkedAsFollowUp
                    ? 'border-amber-500 bg-amber-500 text-white'
                    : 'border-amber-400 text-amber-600 hover:bg-amber-50',
                ].join(' ')}
              >
                {isMarkedAsFollowUp ? '✓ Follow-up' : 'Follow-up'}
              </button>
            </div>
          </div>

          {/* RELATED PATIENT — same as CreateThreadBtn */}
          <div>
            <FieldLabel>Related Patient</FieldLabel>
            {selectedPatient ? (
              <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800">
                <span>{formatHumanName(selectedPatient.name?.[0])}</span>
                <button
                  type="button"
                  onClick={() => setSelectedPatient(null)}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Change
                </button>
              </div>
            ) : (
              <PatientSelect
                setSelectedPatient={setSelectedPatient}
                placeholder="Search patient…"
                hideAddPatientButton
              />
            )}
          </div>

          {/* MESSAGE */}
          <div>
            <FieldLabel>Message</FieldLabel>
            <div className="flex items-start gap-2">
              <Textarea
                placeholder="Write your message…"
                minRows={5}
                autosize
                className="flex-1"
                classNames={{ input: 'rounded-md border-gray-200 text-sm' }}
                {...form.getInputProps('message')}
                onChange={(event) => {
                  const next = event.currentTarget.value
                  messageRef.current = next
                  form.setFieldValue('message', next)
                }}
              />
              <VoiceInputButton
                ref={voiceInputRef}
                value={form.values.message}
                onChange={(text) => {
                  messageRef.current = text
                  form.setFieldValue('message', text)
                }}
                onErrorChange={setMicError}
              />
            </div>
            {micError && <p className="mt-1 text-xs text-red-600">{micError}</p>}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-gray-200 px-2.5 py-[3px] text-[11px] font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md px-2.5 py-[3px] text-[11px] font-semibold text-white transition-colors disabled:opacity-60"
              style={{ backgroundColor: 'rgb(229, 83, 61)' }}
            >
              {submitting ? 'Sending…' : 'Send Message'}
            </button>
          </div>
        </Stack>
      </form>
    </Modal>
  )
}
